'use client'

import { useState } from 'react'
import { Loader2, ArrowRight } from 'lucide-react'
import { BRAND } from '@/components/marketing/MarketingChrome'

const CATEGORIES = [
  { value: 'general', label: 'General question' },
  { value: 'bug', label: 'Something is broken' },
  { value: 'billing', label: 'Billing issue' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Other' },
]

export default function PublicContactForm() {
  const [form, setForm] = useState({
    subject: '',
    category: 'general',
    contactEmail: '',
    shopDomain: '',
    body: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [ticketId, setTicketId] = useState<string | null>(null)

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const validate = () => {
    const e: Record<string, string> = {}
    if (form.subject.trim().length < 3) e.subject = 'At least 3 characters.'
    if (!form.contactEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.contactEmail = 'Enter a valid email.'
    if (form.body.trim().length < 10) e.body = 'Please give us some detail (min 10 characters).'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const res = await fetch('/api/support/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        setErrors({ _: 'Something went wrong — please try again or email us directly.' })
        return
      }
      const data = await res.json()
      setTicketId(data.id)
    } catch {
      setErrors({ _: 'Something went wrong — please try again or email us directly.' })
    } finally {
      setLoading(false)
    }
  }

  if (ticketId) {
    return (
      <div style={{
        background: '#fff',
        border: `1px solid ${BRAND.rule}`,
        padding: 'clamp(32px, 5vw, 48px)',
        borderRadius: 20,
        textAlign: 'center',
        boxShadow: '0 24px 50px -28px rgba(20,20,20,0.18)',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 999,
          background: 'rgba(72, 169, 166, 0.14)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: BRAND.teal, marginBottom: 18, fontSize: 22, fontWeight: 700,
        }}>✓</div>
        <h3 className="font-display" style={{
          fontWeight: 700,
          fontSize: 'clamp(26px, 3.5vw, 36px)',
          lineHeight: 1.05,
          color: BRAND.ink,
          marginBottom: 12,
        }}>
          Thank you, we&apos;ve got it.
        </h3>
        <p style={{ fontSize: 15, color: BRAND.muted, lineHeight: 1.6, marginBottom: 24, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
          A confirmation has gone to{' '}
          <strong style={{ color: BRAND.ink }}>{form.contactEmail}</strong>.
          Reply to that email to add more detail — it lands on the same thread.
        </p>
        <div className="inline-flex items-center gap-3 py-2 px-4" style={{
          border: `1px solid ${BRAND.rule}`, borderRadius: 999, background: '#fafafa',
        }}>
          <div style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: BRAND.mutedSoft,
          }}>Reference</div>
          <div className="tabular" style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 13, fontWeight: 700, color: BRAND.ink, letterSpacing: '0.10em',
          }}>
            #{ticketId.slice(0, 8).toUpperCase()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} style={{
      background: '#fff',
      border: `1px solid ${BRAND.rule}`,
      padding: 'clamp(24px, 3.5vw, 36px)',
      borderRadius: 20,
      boxShadow: '0 1px 3px rgba(20,20,20,0.04)',
    }}>

        <div className="grid sm:grid-cols-2 gap-8 mb-8">
          <div className="support-field">
            <label htmlFor="sp-email">Your email — required</label>
            <input
              id="sp-email"
              type="email"
              value={form.contactEmail}
              onChange={(e) => set('contactEmail', e.target.value)}
              placeholder="you@example.com"
            />
            {errors.contactEmail && <FieldError>{errors.contactEmail}</FieldError>}
          </div>
          <div className="support-field">
            <label htmlFor="sp-shop">Shop domain — optional</label>
            <input
              id="sp-shop"
              type="text"
              value={form.shopDomain}
              onChange={(e) => set('shopDomain', e.target.value)}
              placeholder="yourstore.myshopify.com"
            />
          </div>
        </div>

        <div className="support-field mb-8">
          <label htmlFor="sp-subj">Subject — required</label>
          <input
            id="sp-subj"
            type="text"
            value={form.subject}
            onChange={(e) => set('subject', e.target.value)}
            placeholder="A brief summary of what's on your mind"
          />
          {errors.subject && <FieldError>{errors.subject}</FieldError>}
        </div>

        <div className="mb-8">
          <div style={{ fontSize: 13, fontWeight: 500, color: BRAND.ink, marginBottom: 10 }}>
            Category
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = form.category === c.value
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => set('category', c.value)}
                  style={{
                    fontSize: 13,
                    padding: '7px 14px',
                    borderRadius: 999,
                    border: `1px solid ${active ? BRAND.indigo : 'rgba(20,20,20,0.12)'}`,
                    background: active ? BRAND.indigo : '#fff',
                    color: active ? '#fff' : BRAND.ink2,
                    fontWeight: active ? 600 : 500,
                    transition: 'all 0.15s',
                  }}
                >
                  {c.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="support-field mb-8">
          <label htmlFor="sp-body">Message — required</label>
          <textarea
            id="sp-body"
            value={form.body}
            onChange={(e) => set('body', e.target.value)}
            placeholder="What's happening, what you expected, what would be most helpful — as much detail as you can manage."
            rows={7}
          />
          {errors.body && <FieldError>{errors.body}</FieldError>}
        </div>

        {errors._ && <FieldError>{errors._}</FieldError>}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2 pt-6" style={{ borderTop: `1px solid ${BRAND.rule}` }}>
          <div style={{ fontSize: 12.5, color: BRAND.mutedSoft, lineHeight: 1.5 }}>
            By submitting, you agree to our reply landing in your inbox.
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-coral"
            style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Sending…</>
            ) : (
              <>Send ticket <ArrowRight size={14} strokeWidth={2.5} /></>
            )}
          </button>
        </div>
    </form>
  )
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 12, color: BRAND.coral, marginTop: 6, fontWeight: 500 }}>
      {children}
    </p>
  )
}
